import type { ProcessingFile, ProcessingEvent, GlobalProcessingStats, FileStatus, UploadResult, PaginatedResponse } from '../../../types/starnexus';
import { apiUrl, apiFetch } from '../../../utils/apiClient';

async function req<T>(path: string, opts?: RequestInit): Promise<T> {
  const res = await apiFetch(apiUrl(path), { headers: { 'Content-Type': 'application/json' }, ...opts });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || `API error ${res.status}`);
  }
  return res.json();
}

export interface FileFilters {
  datasetId?: string;
  status?: FileStatus;
}

export const realProcessingService = {
  async getActiveFiles(): Promise<ProcessingFile[]> {
    const { files } = await req<{ files: ProcessingFile[] }>('/processing/active');
    return files;
  },

  async getStats(): Promise<GlobalProcessingStats> {
    return req('/processing/stats');
  },

  async listFiles(filters?: FileFilters, page = 1, pageSize = 10): Promise<PaginatedResponse<ProcessingFile>> {
    if (filters?.datasetId) {
      const { files } = await req<{ files: ProcessingFile[] }>(`/upload/files/${filters.datasetId}`);
      let result = filters.status ? files.filter((f) => f.status === filters.status) : files;
      const total = result.length;
      const start = (page - 1) * pageSize;
      return { items: result.slice(start, start + pageSize), total, page, pageSize, totalPages: Math.ceil(total / pageSize), hasNext: page * pageSize < total, hasPrevious: page > 1 };
    }
    const files = await this.getActiveFiles();
    return { items: files, total: files.length, page: 1, pageSize: files.length, totalPages: 1, hasNext: false, hasPrevious: false };
  },

  async uploadFile(datasetId: string, file: File): Promise<UploadResult> {
    const { uploadUrl, fileId } = await req<{ uploadUrl: string; fileId: string }>('/upload/url', {
      method: 'POST', body: JSON.stringify({ datasetId, fileName: file.name, contentType: file.type, fileSize: file.size }),
    });
    try {
      const r = await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type || 'application/octet-stream' } });
      if (!r.ok) throw new Error('S3 upload failed');
    } catch (e: any) {
      return { fileId, fileName: file.name, status: 'failed', progress: 0, error: e.message };
    }
    await req('/upload/confirm', { method: 'POST', body: JSON.stringify({ datasetId, fileId }) });
    await req(`/processing/${fileId}/status`, { method: 'PATCH', body: JSON.stringify({ datasetId, status: 'queued', progress: 0 }) });
    return { fileId, fileName: file.name, status: 'queued', progress: 0 };
  },

  async updateStatus(fileId: string, datasetId: string, status: FileStatus, extra?: Record<string, any>): Promise<void> {
    await req(`/processing/${fileId}/status`, { method: 'PATCH', body: JSON.stringify({ datasetId, status, ...extra }) });
  },

  async retryFile(fileId: string, datasetId: string): Promise<void> {
    await req(`/processing/${fileId}/retry`, { method: 'POST', body: JSON.stringify({ datasetId }) });
  },

  async getEvents(datasetId?: string, limit = 20): Promise<ProcessingEvent[]> {
    const p = new URLSearchParams({ limit: String(limit) });
    if (datasetId) p.set('datasetId', datasetId);
    const { events } = await req<{ events: ProcessingEvent[] }>(`/processing/events?${p}`);
    return events;
  },

  async logEvent(datasetId: string, eventType: string, extra?: Record<string, any>): Promise<void> {
    await req('/processing/events', { method: 'POST', body: JSON.stringify({ datasetId, eventType, ...extra }) });
  },

  reset(): void {},
};
